import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../contexts/PermissionContext';
import crmKpiService from '../../services/crmKpiService';
import type { CrmKpiResumenPanel } from '../../types/crm';
import { ROUTES } from '../../config/routes';
import { formatCurrency } from '../../utils/formatters';

const Metric: React.FC<{ label: string; value: string; accent?: string }> = ({
  label,
  value,
  accent = '#1976d2',
}) => (
  <Box>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 700, color: accent, mt: 0.25, lineHeight: 1.2 }}>
      {value}
    </Typography>
  </Box>
);

const DashboardCrmWidget: React.FC = () => {
  const navigate = useNavigate();
  const { canModule } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CrmKpiResumenPanel | null>(null);

  useEffect(() => {
    if (!canModule('crm')) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const resumen = await crmKpiService.getResumenPanel();
        if (!cancelled) setData(resumen);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canModule]);

  if (!canModule('crm')) return null;
  if (!loading && !data) return null;

  return (
    <Card
      sx={{
        p: 2.5,
        height: '100%',
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid #e0e0e0',
      }}
    >
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <StackHeader
          onAgenda={() => navigate(ROUTES.crm.agenda)}
          onKpis={() => navigate(ROUTES.crm.kpis)}
        />
        {loading || !data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6} md={4} lg>
              <Metric label="Forecast ponderado" value={formatCurrency(data.forecastPonderado)} />
            </Grid>
            <Grid item xs={6} sm={6} md={4} lg>
              <Metric
                label="Deals ganados"
                value={String(data.dealsGanados)}
                accent="#2e7d32"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={4} lg>
              <Metric
                label="Deals perdidos"
                value={String(data.dealsPerdidos)}
                accent="#d32f2f"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={4} lg>
              <Metric
                label="Monto ganado"
                value={formatCurrency(data.montoGanado)}
                accent="#2e7d32"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={4} lg>
              <Metric
                label="Actividades pendientes (semana)"
                value={String(data.actividadesPendientesSemana)}
                accent="#ed6c02"
              />
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

const StackHeader: React.FC<{ onAgenda: () => void; onKpis: () => void }> = ({
  onAgenda,
  onKpis,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      mb: 0.5,
    }}
  >
    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976d2' }}>
      CRM
    </Typography>
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button size="small" variant="outlined" onClick={onAgenda} sx={{ textTransform: 'none' }}>
        Ver agenda
      </Button>
      <Button size="small" variant="text" onClick={onKpis} sx={{ textTransform: 'none' }}>
        KPIs
      </Button>
    </Box>
  </Box>
);

export default DashboardCrmWidget;
